<script>
import Breadcrumbs from '@/components/general/Breadcrumbs'
import Loading from '@/components/general/Loading'
import ToggleableListItem from '@/components/general/ToggleableListItem'
import { redirectToSsoAdminConsent } from '@/support/sso'

const TENANT_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/

export default {
  name: 'AdminCenterSso',
  components: {
    Breadcrumbs,
    Loading,
    ToggleableListItem
  },
  data () {
    return {
      loading: true,
      saving: false,
      configured: false,
      form: {
        emailDomain: '',
        azureTenantId: '',
        provisionModeAuto: false
      },
      config: null
    }
  },
  computed: {
    breadcrumbsPaths () {
      return [{ text: 'admin.center:menu.10:title', href: this.$route.path }]
    },
    companyName () {
      return this.selectedWorkspace ? this.selectedWorkspace.name : ''
    },
    emailDomainsDisplay () {
      if (!this.config || !this.config.email_domains) return ''
      return this.config.email_domains.join(', ')
    },
    isSsoActive () {
      return this.config && this.config.enabled === true
    },
    consentTenantId () {
      return (this.config && this.config.azure_tenant_id) ||
        this.form.azureTenantId.trim().toLowerCase()
    }
  },
  created () {
    this.handleAdminConsentReturn()
    this.loadConfig()
  },
  methods: {
    handleAdminConsentReturn () {
      const consentResult = this.$route.query.admin_consent
      if (!consentResult) return

      if (consentResult === 'success') {
        this.showSuccess('admin.center.sso:success.consent')
      } else if (consentResult === 'error') {
        this.showError('admin.center.sso:error.consent')
      }

      const query = { ...this.$route.query }
      delete query.admin_consent
      this.$router.replace({ query }).catch(() => {})
    },
    normalizeDomain (value) {
      return value.trim().toLowerCase().replace(/^@+/, '').replace(/^\.+/, '')
    },
    loadConfig () {
      this.loading = true
      this.$store.dispatch('attemptGetSsoConfig')
        .then(({ data }) => {
          this.configured = true
          this.config = data
          this.form.emailDomain = (data.email_domains && data.email_domains[0]) || ''
          this.form.azureTenantId = data.azure_tenant_id || ''
          this.form.provisionModeAuto = data.provision_mode === 'auto'
        })
        .catch((err) => {
          if (err.response && err.response.status === 404) {
            this.configured = false
            this.config = null
            return
          }
          this.showError('admin.center.sso:error.load')
        })
        .finally(() => {
          this.loading = false
        })
    },
    showError (key) {
      this.$store.dispatch('attemptSetFeedbackMsg', {
        type: 'error',
        title: this.$t(key)
      })
    },
    showSuccess (key) {
      this.$store.dispatch('attemptSetFeedbackMsg', {
        type: 'success',
        title: this.$t(key)
      })
    },
    buildPayload (overrides = {}) {
      const domain = this.normalizeDomain(this.form.emailDomain)
      const enabled = overrides.enabled !== undefined
        ? overrides.enabled
        : (this.config ? this.config.enabled === true : false)

      return {
        enabled,
        azure_tenant_id: this.form.azureTenantId.trim().toLowerCase(),
        email_domains: domain ? [domain] : [],
        provision_mode: this.form.provisionModeAuto ? 'auto' : 'manual',
        ...overrides
      }
    },
    validateForm () {
      const domain = this.normalizeDomain(this.form.emailDomain)
      if (!domain || !DOMAIN_RE.test(domain)) {
        this.showError('admin.center.sso:error.invalid.domain')
        return false
      }
      if (!TENANT_RE.test(this.form.azureTenantId.trim())) {
        this.showError('admin.center.sso:error.invalid.tenant')
        return false
      }
      return true
    },
    grantAdminConsent () {
      if (!TENANT_RE.test(this.consentTenantId)) {
        this.showError('admin.center.sso:error.invalid.tenant')
        return
      }
      redirectToSsoAdminConsent(this.consentTenantId)
    },
    updateEnabled (value) {
      if (!this.configured) return
      if (value && !this.validateForm()) return

      this.saving = true
      this.$store.dispatch('attemptUpdateSsoConfig', this.buildPayload({ enabled: value }))
        .then(({ data }) => {
          this.config = data
          this.showSuccess('admin.center.sso:success.update')
        })
        .catch((err) => {
          const msg = err.response && err.response.data && err.response.data.msg
          this.showError(this.mapApiError(msg))
        })
        .finally(() => {
          this.saving = false
        })
    },
    updateProvisionMode (value) {
      this.form.provisionModeAuto = value
      if (!this.configured || !this.isSsoActive) return

      this.saving = true
      this.$store.dispatch('attemptUpdateSsoConfig', this.buildPayload({
        provision_mode: value ? 'auto' : 'manual'
      }))
        .then(({ data }) => {
          this.config = data
          this.showSuccess('admin.center.sso:success.update')
        })
        .catch((err) => {
          this.form.provisionModeAuto = !value
          const msg = err.response && err.response.data && err.response.data.msg
          this.showError(this.mapApiError(msg))
        })
        .finally(() => {
          this.saving = false
        })
    },
    mapApiError (msg) {
      const map = {
        invalid_tenant_id: 'admin.center.sso:error.invalid.tenant',
        tenant_required: 'admin.center.sso:error.invalid.tenant',
        email_domains_required: 'admin.center.sso:error.invalid.domain',
        invalid_domain: 'admin.center.sso:error.invalid.domain',
        tenant_conflict: 'admin.center.sso:error.tenant.conflict',
        domain_conflict: 'admin.center.sso:error.domain.conflict'
      }
      return map[msg] || 'admin.center.sso:error.save'
    }
  }
}
</script>
<template>
  <section class="admin-center-sso--container center-small">
    <div class="default-header">
      <breadcrumbs :items="breadcrumbsPaths" />
      <h2>{{ $t('admin.center:menu.10:title') }}</h2>
    </div>

    <loading v-if="loading" />

    <div v-else-if="!configured" class="admin-center-sso--unavailable mt-6">
      <h3 class="admin-center-sso--step-title">{{ $t('admin.center.sso:unavailable.title') }}</h3>
      <p class="admin-center-sso--step-description">{{ $t('admin.center.sso:unavailable.description') }}</p>
    </div>

    <template v-else-if="!isSsoActive">
      <p class="admin-center-sso--description">{{ $t('admin.center.sso:pending.description') }}</p>

      <div class="admin-center-sso--info mt-6">
        <div class="admin-center-sso--info-row">
          <span class="admin-center-sso--info-label">{{ $t('admin.center.sso:email.domain') }}</span>
          <span>{{ emailDomainsDisplay }}</span>
        </div>
        <div class="admin-center-sso--info-row mt-4">
          <span class="admin-center-sso--info-label">{{ $t('admin.center.sso:tenant.id') }}</span>
          <span>{{ config.azure_tenant_id }}</span>
        </div>
      </div>

      <div class="admin-center-sso--step mt-8">
        <h3 class="admin-center-sso--step-title">{{ $t('admin.center.sso:step2.title') }}</h3>
        <p class="admin-center-sso--step-description">{{ $t('admin.center.sso:step2.description') }}</p>

        <v-btn
          class="mt-6 btn transform-unset"
          :color="getPrimaryColor"
          dark
          large
          :disabled="saving"
          @click="grantAdminConsent"
        >
          {{ $t('admin.center.sso:step2.consent') }}
        </v-btn>
      </div>

      <toggleable-list-item
        class="mt-8"
        :item="{
          inputValue: false,
          title: $t('admin.center.sso:enable.toggle'),
          description: $t('admin.center.sso:enable.toggle.description'),
          icon: 'mdi-shield-key-outline',
          isDisabled: saving
        }"
        @change="updateEnabled"
      />
    </template>

    <template v-else>
      <p class="admin-center-sso--description">
        {{ $t('admin.center.sso:configured.description', { company: companyName }) }}
      </p>

      <div class="admin-center-sso--info mt-6">
        <div class="admin-center-sso--info-row">
          <span class="admin-center-sso--info-label">{{ $t('admin.center.sso:email.domain') }}</span>
          <span>{{ emailDomainsDisplay }}</span>
        </div>
        <div class="admin-center-sso--info-row mt-4">
          <span class="admin-center-sso--info-label">{{ $t('admin.center.sso:tenant.id') }}</span>
          <span>{{ config.azure_tenant_id }}</span>
        </div>
      </div>

      <toggleable-list-item
        class="mt-8"
        :item="{
          inputValue: form.provisionModeAuto,
          title: $t('admin.center.sso:auto.provision.toggle'),
          description: $t('admin.center.sso:auto.provision.toggle.description'),
          icon: 'mdi-account-plus-outline',
          isDisabled: saving
        }"
        @change="updateProvisionMode"
      />

      <toggleable-list-item
        class="mt-4"
        :item="{
          inputValue: true,
          title: $t('admin.center.sso:enable.toggle'),
          description: $t('admin.center.sso:enable.toggle.description'),
          icon: 'mdi-shield-key-outline',
          isDisabled: saving
        }"
        @change="updateEnabled"
      />
    </template>
  </section>
</template>
<style lang="scss">
.admin-center-sso--container {
  .admin-center-sso--description {
    @extend .subtitle--1;
    @extend .neutral-medium;
    max-width: 720px;
  }

  .admin-center-sso--step-title {
    font-size: 18px;
    font-weight: 700;
    color: #212121;
    margin-bottom: 8px;
  }

  .admin-center-sso--step-description {
    @extend .subtitle--1;
    @extend .neutral-medium;
    max-width: 720px;
  }

  .admin-center-sso--info {
    background: #fff;
    border: 1px solid rgba(0, 0, 0, 0.06);
    border-radius: 4px;
    padding: 24px;

    .admin-center-sso--info-row {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .admin-center-sso--info-label {
      font-weight: 700;
      color: #212121;
    }
  }
}
</style>
